<template>
  <span ref="elementRef" class="animated-number">{{ displayValue }}</span>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, ref } from "vue";

const props = withDefaults(
  defineProps<{
    start?: number;
    end: number;
    duration?: number;
  }>(),
  {
    start: 0,
    duration: 1000,
  },
);

const displayValue = ref(props.end);
const elementRef = ref<HTMLElement | null>(null);
const hasAnimated = ref(false);
let observer: IntersectionObserver | null = null;

function easeOutQuart(t: number): number {
  return 1 - (1 - t) ** 4;
}

function animate() {
  displayValue.value = props.start;

  const startTime = performance.now();
  const startValue = props.start;
  const endValue = props.end;
  const diff = endValue - startValue;

  function step(currentTime: number) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / props.duration, 1);
    const easedProgress = easeOutQuart(progress);

    displayValue.value = Math.round(startValue + diff * easedProgress);

    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

onMounted(() => {
  if (!elementRef.value) return;

  observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (entry.isIntersecting && !hasAnimated.value) {
        hasAnimated.value = true;
        animate();
        observer?.disconnect();
      }
    },
    { threshold: 0.1 },
  );

  observer.observe(elementRef.value);
});

onUnmounted(() => {
  observer?.disconnect();
});
</script>

<style scoped>
.animated-number {
  font-variant-numeric: tabular-nums;
}
</style>
